/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../hooks';
import { useField } from '@unform/core';
import { PlanoDeContasService } from '../services/api/cadastros/PlanoDeContasService';


// Quantos planos a lista traz. O padrão do sistema (LIMITE_DE_LINHAS = 7)
// mostrava só os primeiros do ATIVO, na ordem de grupo — os de
// receita/despesa (ex.: as vendas) nunca apareciam sem digitar.
const LIMITE_PLANOS = 1000;

type TAutoCompleteOption = {
  id: string;
  label: string;
  grupo: string;
}

interface IAutoCompletePlanoDeContasProps {
  isExternalLoading?: boolean;
}
export const AutoCompletePlanoDeContas: React.FC<IAutoCompletePlanoDeContasProps> = ({ isExternalLoading = false }) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField('idplanoconta');
  const { debounce } = useDebounce();

  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);

  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [erroBusca, setErroBusca] = useState('');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  // Sempre a lista (filtrada pelo que foi digitado) — antes, com um plano já
  // selecionado e nada digitado, buscava SÓ ele pelo id (pra mostrar o nome no
  // campo), e a lista aberta tinha um item só: numa venda nova, que já vem
  // com o plano padrão, não dava pra escolher outro sem digitar. O
  // selecionado é buscado à parte só se não vier na lista (ex.: fora do
  // limite de linhas), pra continuar aparecendo no campo.
  useEffect(() => {
    setIsLoading(true);

    debounce(async () => {
      const paraOpcao = (planoDeContas: { idplanoconta: string; descricao: string; grupo: string }) =>
        ({ id: planoDeContas.idplanoconta, label: planoDeContas.descricao, grupo: planoDeContas.grupo });

      // A árvore inteira (planos de contas são poucas dezenas) — ver LIMITE_PLANOS.
      const result = await PlanoDeContasService.getAlllist(busca, '', LIMITE_PLANOS);
      if (result instanceof Error) {
        // Mostra o motivo na própria lista em vez de só "Sem opções".
        setErroBusca(result.message);
        setIsLoading(false);
        return;
      }
      setErroBusca('');
      const lista = result.data.map(paraOpcao);
      if (selectedId && !lista.some(opcao => opcao.id === selectedId)) {
        const selecionado = await PlanoDeContasService.getAlllist('', selectedId);
        if (!(selecionado instanceof Error)) {
          lista.push(...selecionado.data.map(paraOpcao));
          // groupBy exige as opções em ordem de grupo (senão o grupo repete);
          // o sort é estável, então a ordem dentro de cada grupo se mantém.
          lista.sort((a, b) => (a.grupo || '').localeCompare(b.grupo || ''));
        }
      }
      setIsLoading(false);
      setOpcoes(lista);
    });
  }, [busca, selectedId]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.id === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);
  
  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText={erroBusca ? `Erro ao carregar os planos: ${erroBusca}` : 'Sem opções'}
      loadingText='Carregando...'

      disablePortal

      options={opcoes}
      groupBy={(opcoes) => opcoes.grupo}
      loading={isLoading}
      disabled={isExternalLoading}
      value={autoCompleteSelectedOption}
      // Só busca sozinho por `reason==='input'` — ver AutoCompleteMunicipio.tsx pro caso
      // completo do loop que isso evita (reset automático do MUI virando busca).
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setBusca(newValue);
      }}
      onChange={(_, newValue) => { setSelectedId(newValue?.id); setBusca(''); clearError(); }}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label="Plano de Contas"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};